import { ElementRef, Renderer2 } from '@angular/core';
// todo: при удалении gas-datepicker нужно будет не забыть перенести интерфейс
import {
  GasDatepicker,
  GasPickerDay,
  NextMonthDate,
  PreviousMonthDate,
} from '../gas-datepicker/gas-datepicker.interface';

const DATE_SETTING = {
  minIndexDay: 1,
  weekLast: 7,
  minLength: 42,
  minMonthIndex: 0,
  maxMonthIndex: 11,
};

const DATEPICKER_POSITION = {
  offsetTop: 50,
  inputHeight: 30,
};

export namespace GasDatepickerInputHelper {

  /** позиционируем календарь в зависимости от положения на странице элемента триггера */
  export function setDatepickerPosition(datepickerWrapper: ElementRef, renderer: Renderer2): void {

    const datepickerRef = ( datepickerWrapper.nativeElement as HTMLElement ).firstElementChild;
    const datepickerCoords = datepickerRef.getBoundingClientRect();
    const pageHeight = window.innerHeight;
    const pageWidth = window.innerWidth;

    /** помещаем датапикер над элементом если не помещается снизу */
    if (pageHeight - datepickerCoords.bottom < 0) {
      renderer.setStyle(datepickerRef, 'top', `inherit`);
      renderer.setStyle(datepickerRef, 'bottom', `${DATEPICKER_POSITION.inputHeight}px`);
    } else {
      renderer.setStyle(datepickerRef, 'top', `${DATEPICKER_POSITION.offsetTop}px`);
    }

    if (pageWidth - datepickerCoords.right < 0) {
      renderer.setStyle(datepickerRef, 'left', `inherit`);
      renderer.setStyle(datepickerRef, 'right', '0');
    } else {
      renderer.setStyle(datepickerRef, 'left', '0');
    }
  }

  export function checkToDay(selectDay: Date): boolean {
    const currentDay = new Date();

    return selectDay.getDate() === currentDay.getDate()
      && selectDay.getMonth() === currentDay.getMonth()
      && selectDay.getFullYear() === currentDay.getFullYear();
  }

  /**
   * Создание объекта с корректными значениями для заполнения календаря
   *
   * @param month - текущий выбранный месяц
   * @param year - текущий выбранный год
   * @param day - текущий выбранный день
   */
  export function createCurrentDateObject(
    day: Date = new Date(),
    month: number = day.getMonth(),
    year: number = day.getFullYear(),
  ): GasDatepicker {
    return {
      currentDay: day,
      currentMonth: month,
      currentYear: year,
      lastDay: GasDatepickerInputHelper.getLastDay(month, year),
      indexDay: GasDatepickerInputHelper.getIndexFirstDay(month, year),
    };
  }

  /** Устанавливает последний день текущего месяца */
  export function getLastDay(month: number, year: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  /** Устанавливает индекс первого дня текущего месяца (Пн, Вт....Вс) */
  export function getIndexFirstDay(month: number, year: number): number {
    return new Date(year, month, 1).getDay();
  }

  /** Формирует массив для заполнения datepickera */
  export function setDaysInMonth(dateSetting: GasDatepicker, minDate?: Date, maxDate?: Date): GasPickerDay[] {
    const dateObject = GasDatepickerInputHelper.currentMonthDays(dateSetting, minDate, maxDate);
    dateObject.unshift(...GasDatepickerInputHelper.previusMonthDays(dateSetting, minDate, maxDate));
    dateObject.push(...GasDatepickerInputHelper.nextMonthDays(dateObject, dateSetting, minDate, maxDate));
    return dateObject;
  }

  /**
   * Заполняет массив днями текущего месяца
   */
  export function currentMonthDays(dateSetting: GasDatepicker, minDate?: Date, maxDate?: Date): GasPickerDay[] {
    const daysInMonth: GasPickerDay[] = [];
    for (let i = 1; i <= dateSetting.lastDay; i++) {
      const currentDate = new Date(dateSetting.currentYear, dateSetting.currentMonth, i);
      daysInMonth.push({
        value: new Date(dateSetting.currentYear, dateSetting.currentMonth, i),
        isActive: GasDatepickerInputHelper.isActiveDay(currentDate, dateSetting) ? true : false,
        otherMonth: false,
        isCurrent: this.checkCurrendDay(currentDate, minDate, maxDate),
      });
    }

    return daysInMonth;
  }

  /**
   * Добавляет в начало массива дни, если месяц начинается не с понедельника
   *
   * @param dateSetting - объект с текущими корректнми значениями даты (понедельник = 1)
   */
  export function previusMonthDays(dateSetting: GasDatepicker, minDate?: Date, maxDate?: Date): GasPickerDay[] {
    const daysPreviusMonth: GasPickerDay[] = [];
    if (dateSetting.indexDay !== DATE_SETTING.minIndexDay) {
      const previusMonthDate: PreviousMonthDate = GasDatepickerInputHelper.getPreviousMonthDate(dateSetting);

      for (let i = 0; i < previusMonthDate.currectIndex - 1; i++) {
        let day = previusMonthDate.previusMonthLastDay - i;
        const currentDate = new Date(dateSetting.currentYear, previusMonthDate.previusMonth, day);

        day++;
        daysPreviusMonth.unshift({
          value: currentDate,
          otherMonth: true,
          isActive: false,
          monthIndex: -1,
          isCurrent: GasDatepickerInputHelper.checkCurrendDay(currentDate, minDate, maxDate),
        });
      }
    }

    return daysPreviusMonth;
  }

  /**
   * Добавляет в конец массива дни, если в pickere осталось свободное место
   * (на экране всегда должно показываться 35 дней - 5 рядов*7дней)
   */
  export function nextMonthDays(
    daysInMonth: GasPickerDay[],
    dateSetting: GasDatepicker,
    minDate: Date,
    maxDate: Date,
  ): GasPickerDay[] {
    const daysNextMonth: GasPickerDay[] = [];
    const nextMonthDate: NextMonthDate = GasDatepickerInputHelper.getNextMonthDate(dateSetting);

    let iterator = 0;
    let i = daysInMonth.length;
    while (i < DATE_SETTING.minLength) {
      const day = nextMonthDate.previusMonthLastDay + iterator;
      const currentDate = new Date(dateSetting.currentYear, nextMonthDate.nextMonth, day);
      daysNextMonth.push({
        value: currentDate,
        otherMonth: true,
        isActive: false,
        monthIndex: 1,
        isCurrent: GasDatepickerInputHelper.checkCurrendDay(currentDate, minDate, maxDate),
      });
      iterator++;
      i++;
    }

    return daysNextMonth;
  }

  /**
   * Проверяет выбранный день, что бы выделить его в календаре
   * сравнение строго на день, месяц и год
   */
  export function isActiveDay(date: Date, dateSetting: GasDatepicker): boolean {
    return date.getDate() === dateSetting.currentDay.getDate() &&
      dateSetting.currentMonth === dateSetting.currentDay.getMonth() &&
      dateSetting.currentYear === dateSetting.currentDay.getFullYear();
  }

  export function checkCurrendDay(currentDate?: Date, minDate?: Date, maxDate?: Date): boolean {
    currentDate?.setHours(0, 0, 0, 0);
    minDate?.setHours(0, 0, 0, 0);
    maxDate?.setHours(0, 0, 0, 0);
    if (currentDate?.getTime() < minDate?.getTime() || currentDate?.getTime() > maxDate?.getTime()) {
      return false;
    }

    return true;
  }

  /** Возвращает объект с данными о предыдущем месяце */
  export function getPreviousMonthDate(dateSetting: GasDatepicker): PreviousMonthDate {
    const previusMonth = dateSetting.currentMonth - DATE_SETTING.minIndexDay < DATE_SETTING.minMonthIndex ?
      DATE_SETTING.maxMonthIndex : dateSetting.currentMonth - DATE_SETTING.minIndexDay;
    const previusMonthLastDay = new Date(dateSetting.currentYear, previusMonth + 1, 0).getDate();
    const currectIndex = dateSetting.indexDay < DATE_SETTING.minIndexDay ? DATE_SETTING.weekLast : dateSetting.indexDay;

    return {
      previusMonth,
      previusMonthLastDay,
      currectIndex,
    };
  }

  /** Возвращает объект с данными о следующем месяце */
  export function getNextMonthDate(dateSetting: GasDatepicker): NextMonthDate {
    const nextMonth = dateSetting.currentMonth + 1 > 11 ? 0 : dateSetting.currentMonth + 1;
    const previusMonthLastDay = new Date(dateSetting.currentYear, nextMonth + 1, 1).getDate();

    return {
      nextMonth,
      previusMonthLastDay,
    };
  }
}
